import { Model, type ModelProps } from '@/infra/model'
import { Id } from '@/infra/id'
import { computed, observable } from 'mobx'
import type { JobTaxDataImportStatus } from '@/features/jobs/details/tax-type/info-gathering/measure-one/job-tax-data-import-status'
import type { MeasureOneDataRequestStatus } from '@/features/jobs/details/tax-type/info-gathering/measure-one/data-request-status'
import type { WellKnownDocumentTypeShortId } from '@/features/jobs/details/tax-type/info-gathering/measure-one/mappers'
import { unixEpoch } from '@/utils/date-util'
import { groupBy, map, maxBy } from 'lodash-es'

export type JobTaxDataImportProps = ModelProps<JobTaxDataImport>

export class JobTaxDataImport extends Model<JobTaxDataImport> {
  id = Id.none
  @observable accessor jobId = Id.none
  @observable accessor workspaceId = Id.none
  @observable accessor status: JobTaxDataImportStatus = 'Unspecified'
  @observable accessor individualId = Id.none
  @observable accessor dataRequests: MeasureOneDataRequest[] = []
  @observable accessor dateCreated: Date = unixEpoch()
  @observable accessor dateModified: Date = unixEpoch()

  /**
   * latest items filtered by data request status equal to "itemProcessed" and grouped by data source.
   */
  @computed get items() {
    const processedItems = this.dataRequests.filter(
      (dataRequest) =>
        dataRequest.status === 'ItemsProcessed' && dataRequest.items.length > 0,
    )

    return map(
      groupBy(processedItems, 'dataSource.id'),
      (groupedDataRequests) =>
        maxBy(groupedDataRequests, (dR) => dR.dateItemsProcessed),
    ).flatMap((dataRequest) => {
      if (!dataRequest) {
        return []
      }

      return dataRequest.items.map((item) => ({
        ...item,
        source: dataRequest.dataSource.name,
        dataRequestId: dataRequest.id,
      }))
    })
  }
}

export interface MeasureOneDataRequest {
  id: Id
  status: MeasureOneDataRequestStatus
  experienceId: Id
  requestedByUserPublicId: Id
  dateStarted: Date
  dataSource: {
    id: Id
    name: string
  }
  dateConnectionCompleted: Date
  dateItemsProcessed: Date
  items: MeasureItem[]
}

export interface MeasureItem {
  id: Id
  sourceDataType: WellKnownDocumentTypeShortId
}
